import axiosInstance from "./axiosInstance";

export const getAllSales = (archived = false) =>
    axiosInstance.get(`/Sales/GetAllSales?archived=${archived}`);

// "Send to Sales Bot": stash the exact sale payload, get a short code to hand to Telegram.
export const stashBotPayload = (payload) =>
    axiosInstance.post("/Sales/StashBotPayload", payload);

export const getAllSalesByAgentId = (agentId, archived = false) =>
    axiosInstance.get(`/Sales/GetAllSalesByAgentId?agentId=${agentId}&archived=${archived}`);

export const getAllSalesByGroupName = (groupName, archived = false) =>
    // encodeURIComponent so group names with special chars (e.g. "Don & Alice")
    // don't break the query string — the '&' was being read as a param separator.
    axiosInstance.get(`/Sales/GetAllSalesByGroupName?groupName=${encodeURIComponent(groupName)}&archived=${archived}`);

export const getTotalFinalPriceByGroupLast30Days = () =>
    axiosInstance.get("/Sales/GetTotalFinalPriceByGroupLast30Days");

export const createSales = (salesData) =>
    axiosInstance.post("/Sales/CreateSales", salesData);

export const getSalesLineItems = (salesId) =>
    axiosInstance.get(`/Sales/GetSalesLineItems?salesId=${salesId}`);

export const updateSalesByAdmin = (salesData) =>
    axiosInstance.put("/Sales/UpdateSalesByAdmin", salesData);

// Project Timeline milestone update (admin / group-admin). Sends the 6 manual dates.
export const updateSalesTimeline = (payload) =>
    axiosInstance.put("/Sales/UpdateSalesTimeline", payload);

// The Scheduling board — every job the caller may see, with its dates and its payment
// position. The API scopes it (all / group / own), so there is nothing to pass.
export const getSchedule = () => axiosInstance.get("/Sales/GetSchedule");

// The customer's half of an installation date. Office roles only; the API refuses to
// confirm a date that does not exist, and clears the confirmation whenever the date moves.
export const setInstallationConfirmed = (salesId, confirmed) =>
    axiosInstance.put(`/Sales/SetInstallationConfirmed?salesId=${salesId}&confirmed=${confirmed}`);

// The site visit's "being arranged" state — the one milestone with no date to key while it
// is in progress. Office roles only; refused once a real site-visit date exists.
export const setSiteVisitArranging = (salesId, arranging) =>
    axiosInstance.put(`/Sales/SetSiteVisitArranging?salesId=${salesId}&arranging=${arranging}`);

// One endpoint for the whole Site Visit block — field is gated | plates |
// needapproval | mgmt | sitevisitrequired.
export const setSiteVisitFlag = (salesId, field, on) =>
    axiosInstance.put(`/Sales/SetSiteVisitFlag?salesId=${salesId}&field=${field}&on=${on}`);

export const importHistoricalSales = (salesData) =>
    axiosInstance.post("/Sales/ImportHistoricalSales", { sales: salesData }, { timeout: 300000 });

export const getTopAgents = (month, year) =>
    axiosInstance.get(`/Sales/GetTopAgents?month=${month}&year=${year}`);

// Hall of fame — each agent's best single-month revenue, all-time (top 10).
export const getBestMonthlySales = () =>
    axiosInstance.get("/Sales/GetBestMonthlySales");

export const getRecentSales = (limit = 10) =>
    axiosInstance.get(`/Sales/GetRecentSales?limit=${limit}`);

export const getCompanyGoalTotal = (year = 2026) =>
    axiosInstance.get(`/Sales/GetCompanyGoalTotal?year=${year}`);

export const getSalesById = (salesId) =>
    axiosInstance.get(`/Sales/GetSalesById?salesId=${salesId}`);

export const archiveSale = (salesId) =>
    axiosInstance.put(`/Sales/ArchiveSale?salesId=${salesId}`);

export const getSalesDocuments = (salesId) =>
    axiosInstance.get(`/Sales/GetSalesDocuments?salesId=${salesId}`);

export const uploadSalesDocument = (formData) =>
    axiosInstance.post('/Sales/UploadSalesDocument', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

export const deleteSalesDocument = (salesDocumentId) =>
    axiosInstance.delete(`/Sales/DeleteSalesDocument?salesDocumentId=${salesDocumentId}`);

// types: optional array of form types to regenerate (e.g. ['quotation']); omit for the full set.
export const generateSaleForms = (salesId, signed = false, types = null) => {
    const t = Array.isArray(types) && types.length ? `&types=${encodeURIComponent(types.join(','))}` : '';
    return axiosInstance.post(`/Sales/GenerateSaleForms?salesId=${salesId}&signed=${signed}${t}`);
};

// regenerate=false → idempotent get-or-create (auto-load the existing link, no new token).
// regenerate=true  → force a fresh token (the "Regenerate" / "Re-sign" button).
export const generateSignLink = (salesId, regenerate = false) =>
    axiosInstance.post(`/Sales/GenerateSignLink?salesId=${salesId}&regenerate=${regenerate}`);

// Sales-person (agent) signature — residential dual-party signing. body = { salesPersonSignaturePng }
export const agentSign = (salesId, body) =>
    axiosInstance.post(`/Sales/AgentSign?salesId=${salesId}`, body);

// Possible-duplicate check for the create forms (WARN-only, never blocks save).
export const checkDuplicateSale = ({ name, phone, ic, excludeSalesId } = {}) =>
    axiosInstance.post('/Sales/CheckDuplicateSale', {
        name: name || null,
        phone: phone || null,
        ic: ic || null,
        excludeSalesId: excludeSalesId ?? null,
    });